import {
  DEFAULT_EXERCISE_FILTERS,
  IExerciseFilters,
  IExerciseFiltersStorageValue,
  cloneExerciseFilters,
} from './types';

export const EXERCISE_FILTERS_PAGE_KEYS = {
  public: 'exercises:public',
  my: 'exercises:my',
  favorite: 'exercises:favorite',
  publishedByUsers: 'exercises:published-by-users',
} as const;

export type ExerciseFiltersPageKey =
  (typeof EXERCISE_FILTERS_PAGE_KEYS)[keyof typeof EXERCISE_FILTERS_PAGE_KEYS];

const EXERCISE_FILTERS_STORAGE_PREFIX = 'sg-exercise-filters:';
const EXERCISE_FILTERS_STORAGE_VERSION = 1;

function getExerciseFiltersStorageKey(pageKey: string): string {
  return `${EXERCISE_FILTERS_STORAGE_PREFIX}${pageKey}`;
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}

function isValidStorageValue(
  value: unknown,
): value is IExerciseFiltersStorageValue {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const maybeValue = value as IExerciseFiltersStorageValue;
  const filters = maybeValue.filters;
  return (
    maybeValue.version === EXERCISE_FILTERS_STORAGE_VERSION &&
    !!filters &&
    typeof filters === 'object' &&
    isStringArray(filters.categories) &&
    isStringArray(filters.muscles) &&
    isStringArray(filters.equipment) &&
    (filters.developedSkills === undefined ||
      isStringArray(filters.developedSkills))
  );
}

export function loadExerciseFilters(pageKey: string): IExerciseFilters {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(getExerciseFiltersStorageKey(pageKey));
  } catch {
    return cloneExerciseFilters(DEFAULT_EXERCISE_FILTERS);
  }

  if (!raw) {
    return cloneExerciseFilters(DEFAULT_EXERCISE_FILTERS);
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (isValidStorageValue(parsed)) {
      const f = parsed.filters;
      return cloneExerciseFilters({
        ...f,
        developedSkills: f.developedSkills ?? [],
      });
    }
  } catch {
    return cloneExerciseFilters(DEFAULT_EXERCISE_FILTERS);
  }

  return cloneExerciseFilters(DEFAULT_EXERCISE_FILTERS);
}

export function persistExerciseFilters(
  pageKey: string,
  filters: IExerciseFilters,
): void {
  const value: IExerciseFiltersStorageValue = {
    version: EXERCISE_FILTERS_STORAGE_VERSION,
    filters,
  };
  try {
    localStorage.setItem(
      getExerciseFiltersStorageKey(pageKey),
      JSON.stringify(value),
    );
  } catch {
    return;
  }
}
